import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import SectionReveal from "@/components/effects/SectionReveal";
import { PROJECTS } from "@/data/content";
import { TID } from "@/constants/testIds";

const Projects = () => {
    return (
        <section id="projects" className="relative py-24 md:py-40">
            <div className="container-x">
                <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
                    <SectionReveal>
                        <p className="overline">03 — Featured work</p>
                        <h2
                            className="mt-4 text-3xl font-medium tracking-tight text-white sm:text-4xl md:text-5xl"
                            style={{ fontFamily: "'General Sans', sans-serif" }}
                        >
                            Projects, shipped in production.
                        </h2>
                    </SectionReveal>
                    <SectionReveal delay={0.15}>
                        <p className="max-w-md text-sm text-brand-muted md:text-base">
                            A small selection of digital products live on the internet today — each with its own
                            audience, funnel, and metrics.
                        </p>
                    </SectionReveal>
                </div>

                <div className="mt-14 space-y-16 md:space-y-24">
                    {PROJECTS.map((p, i) => {
                        const flip = i % 2 === 1;
                        return (
                            <SectionReveal key={p.id} delay={0.05 * i}>
                                <article
                                    data-testid={TID.projectCard(p.id)}
                                    className="group grid items-center gap-10 md:grid-cols-12 md:gap-16"
                                >
                                    <div className={`md:col-span-7 ${flip ? "md:order-2" : ""}`}>
                                        <a
                                            href={p.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="relative block overflow-hidden rounded-3xl border border-white/[.08] bg-[#0b0b0b]"
                                        >
                                            <div className="pointer-events-none absolute inset-0 z-10 rounded-3xl ring-1 ring-inset ring-white/[.05]" />
                                            <div
                                                aria-hidden
                                                className="pointer-events-none absolute -inset-1 z-0 opacity-0 blur-2xl transition-opacity duration-700 group-hover:opacity-70"
                                                style={{
                                                    background:
                                                        "radial-gradient(60% 60% at 50% 50%, rgba(59,130,246,0.35), rgba(6,182,212,0) 70%)"
                                                }}
                                            />
                                            <motion.img
                                                src={p.image}
                                                alt={`${p.title} — screenshot`}
                                                loading="lazy"
                                                decoding="async"
                                                width={1600}
                                                height={1000}
                                                className="relative z-[1] block aspect-[16/10] w-full object-cover object-top"
                                                initial={{ scale: 1.02 }}
                                                whileHover={{ scale: 1.06 }}
                                                transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                                            />
                                            {/* Glass reflection strip */}
                                            <div
                                                aria-hidden
                                                className="pointer-events-none absolute inset-y-0 -left-1/3 z-[2] w-1/3 rotate-6 bg-gradient-to-r from-transparent via-white/[.10] to-transparent opacity-0 transition-all duration-700 group-hover:left-full group-hover:opacity-100"
                                            />
                                        </a>
                                    </div>
                                    <div className={`md:col-span-5 ${flip ? "md:order-1" : ""}`}>
                                        <p className="overline">{p.category}</p>
                                        <h3
                                            className="mt-4 text-3xl font-medium tracking-tight text-white md:text-4xl"
                                            style={{ fontFamily: "'General Sans', sans-serif" }}
                                        >
                                            {p.title}
                                        </h3>
                                        <p className="mt-4 text-base text-brand-muted md:text-lg">{p.description}</p>
                                        <div className="mt-6 flex flex-wrap gap-2">
                                            {p.tech.map((t) => (
                                                <span key={t} className="chip">
                                                    {t}
                                                </span>
                                            ))}
                                        </div>
                                        <a
                                            data-testid={TID.projectVisit(p.id)}
                                            href={p.url}
                                            target="_blank"
                                            rel="noreferrer"
                                            className="mt-8 inline-flex items-center gap-2 rounded-full border border-white/10 bg-white/[.03] px-5 py-3 text-sm font-medium text-white transition-all hover:border-white/25 hover:bg-white/[.06]"
                                        >
                                            Visit website
                                            <ArrowUpRight className="h-4 w-4" />
                                        </a>
                                    </div>
                                </article>
                            </SectionReveal>
                        );
                    })}
                </div>
            </div>
        </section>
    );
};

export default Projects;
